/**
 * Quantity stepper for the family order surface — the "+ / −" pattern from
 * consumer ordering apps. At 0 it's a single round "+"; once a quantity exists
 * it becomes "− N +". Each tap calls `onChange` with the next value, which the
 * caller writes straight to the cart (no draft step).
 */

import { Minus, Plus, Trash2 } from 'lucide-react'
import type { ReactElement } from 'react'

export interface QtyStepperProps {
  max?: number
  value: number
  onChange: (next: number) => void
  disabled?: boolean
}

const BTN =
  'inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40'

export function QtyStepper({ value, onChange, disabled = false, max = Infinity }: QtyStepperProps): ReactElement {
  if (value <= 0) {
    return (
      <button
        type="button"
        aria-label="Add one"
        disabled={disabled || value >= max}
        onClick={() => onChange(1)}
        className={BTN}>
        <Plus className="h-4 w-4" />
      </button>
    )
  }
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        aria-label="Remove one"
        disabled={disabled}
        onClick={() => onChange(value - 1)}
        className={BTN}>
        <Minus className="h-4 w-4" />
      </button>
      <span className="min-w-6 text-center text-base font-semibold tabular-nums text-foreground">{value}</span>
      <button
        type="button"
        aria-label="Add one"
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        className={BTN}>
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )
}

export interface QtyPillProps {
  value: number
  onChange: (next: number) => void
  /** Disables the whole pill (e.g. while checkout is submitting). */
  disabled?: boolean
  /** Disables only "+" — e.g. an unpriced line that can be removed but not grown. */
  incrementDisabled?: boolean
}

const PILL_BTN =
  'grid h-9 w-9 place-items-center rounded-full text-foreground transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40'

/**
 * Cart-line quantity pill — the consumer-app convention (Uber Eats) for a line
 * that is already in the cart: a single rounded pill of [trash / −] [N] [+].
 * At quantity 1 the left control is a trash can and tapping it removes the
 * line (`onChange(0)` — the cart's `upsertLine` drops lines at quantity <= 0);
 * at 2+ it is a minus. The right "+" always increments.
 */
export function QtyPill({ value, onChange, disabled = false, incrementDisabled = false }: QtyPillProps): ReactElement {
  const removesLine = value <= 1
  return (
    <div className="inline-flex shrink-0 items-center rounded-full bg-muted">
      <button
        type="button"
        aria-label={removesLine ? 'Remove item' : 'Remove one'}
        disabled={disabled}
        onClick={() => onChange(value - 1)}
        className={PILL_BTN}>
        {removesLine ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
      </button>
      <span className="min-w-5 text-center text-sm font-semibold tabular-nums text-foreground">{value}</span>
      <button
        type="button"
        aria-label="Add one"
        disabled={disabled || incrementDisabled}
        onClick={() => onChange(value + 1)}
        className={PILL_BTN}>
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )
}
